import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PhoneInput, isValidPhoneNumber } from '@/components/ui/phone-input';
import { toast } from '@/hooks/use-toast';
import { useIniciarConversa } from '@/hooks/useMensagens';
import { sanitizeSupabaseError } from '@/lib/errorHandler';

interface NovaConversaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NovaConversaDialog({ open, onOpenChange }: NovaConversaDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const iniciar = useIniciarConversa();

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [erroTelefone, setErroTelefone] = useState<string | undefined>();

  const reset = () => {
    setNome('');
    setTelefone('');
    setErroTelefone(undefined);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!telefone || !isValidPhoneNumber(telefone)) {
      setErroTelefone(t('common.validation.invalidPhone'));
      return;
    }
    iniciar.mutate(
      { nome: nome.trim(), telefone },
      {
        onSuccess: (res) => {
          if (res.alreadyExisted) {
            toast({ description: t('panel.newConversationDialog.successExisted') });
          } else if (res.sendFailed) {
            toast({
              variant: 'destructive',
              title: t('panel.newConversationDialog.sendFailedTitle'),
              description: t('panel.newConversationDialog.sendFailedDescription'),
            });
          } else {
            toast({ description: t('panel.newConversationDialog.successSent') });
          }
          handleOpenChange(false);
          navigate(`/painel?cliente=${res.clienteId}`);
        },
        onError: (error) => {
          toast({
            variant: 'destructive',
            title: t('panel.newConversationDialog.errorTitle'),
            description: sanitizeSupabaseError(error),
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('panel.newConversationDialog.title')}</DialogTitle>
          <DialogDescription>{t('panel.newConversationDialog.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nc-nome">{t('panel.newConversationDialog.nameLabel')} *</Label>
            <Input
              id="nc-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={t('panel.newConversationDialog.namePlaceholder')}
              maxLength={100}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nc-telefone">{t('panel.newConversationDialog.phoneLabel')} *</Label>
            <PhoneInput
              value={telefone}
              onChange={(value) => { setTelefone(value); setErroTelefone(undefined); }}
              defaultCountry="PT"
              placeholder="912 345 678"
              error={!!erroTelefone}
            />
            {erroTelefone && <p className="text-sm text-destructive">{erroTelefone}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="submit" disabled={iniciar.isPending || !nome.trim()}>
              {iniciar.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {iniciar.isPending
                ? t('panel.newConversationDialog.submitting')
                : t('panel.newConversationDialog.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
